import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectClient, selectMaybeClientPartner } from '@client/model';

type PaymentOption = 'Single Pay' | '10-Pay' | 'Annually' | 'Monthly';

type BuyerOption = {
  key: '1-buyer' | '2-buyer';
  label: string;
  maritalClass: string;
};

export const BUYER_OPTIONS: Record<'1-buyer' | '2-buyer', BuyerOption> = {
  '1-buyer': {
    key: '1-buyer',
    label: '1 Buyer',
    maritalClass: 'Married 1 Buy',
  },
  '2-buyer': {
    key: '2-buyer',
    label: '2 Buyers',
    maritalClass: 'Married 2 Buy',
  },
};

const MARITAL_STATUS_MAPPING: Record<string, string> = {
  'Never married': 'Single',
  'Married': 'Married',
  'Married, spouse absent': 'Married',
  'Partnered': 'Married',
  'Separated': 'Single',
  'Divorced': 'Single',
  'Widowed': 'Single',
  "I don't know / prefer not to answer": 'Single',
};

export const mapMaritalStatusToTraditionalLTC = (maritalStatus?: string): string => {
  if (!maritalStatus) return 'Single';
  return MARITAL_STATUS_MAPPING[maritalStatus] || 'Single';
};

export const getMaritalClassForTraditionalLTC = (
  paymentOption: string,
  selectedBuyerOption: BuyerOption,
  hasPartnerWithDemographics: boolean,
  clientMaritalStatus?: string,
): string => {
  const mappedMaritalStatus = mapMaritalStatusToTraditionalLTC(clientMaritalStatus);
  if (['Monthly', 'Annually'].includes(paymentOption)) {
    if (mappedMaritalStatus === 'Married' || hasPartnerWithDemographics) {
      return selectedBuyerOption.maritalClass;
    } else {
      return 'Single';
    }
  } else {
    return mappedMaritalStatus;
  }
};

interface UseShopInsuranceStateProps {
  hideSinglePayAnd10Pay?: boolean;
  providedSelectedTab?: ShopInsuranceTab;
  providedPaymentOption?: PaymentOption;
  providedBuyerOption?: '1-buyer' | '2-buyer';
}

export function useShopInsuranceState(props?: UseShopInsuranceStateProps) {
  const { hideSinglePayAnd10Pay = false } = props || {};
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);

  const hasPartnerWithDemographics = useMemo(() => {
    return Boolean(
      client?.partnerClientId &&
        clientPartner?.intakeSurvey?.clientBirthDate &&
        clientPartner?.intakeSurvey?.clientGenderAtBirth &&
        client?.intakeSurvey?.clientGenderAtBirth !==
          clientPartner?.intakeSurvey?.clientGenderAtBirth,
    );
  }, [
    client?.partnerClientId,
    client?.intakeSurvey?.clientGenderAtBirth,
    clientPartner?.intakeSurvey,
  ]);

  const defaultPaymentOption: PaymentOption = props?.providedPaymentOption
    ? props?.providedPaymentOption
    : hideSinglePayAnd10Pay
      ? 'Annually'
      : 'Single Pay';

  const [selectedTab, setSelectedTab] = useState<ShopInsuranceTab>(
    props?.providedSelectedTab
      ? props?.providedSelectedTab
      : hasPartnerWithDemographics
        ? 'joint'
        : 'single',
  );
  const [selectedBuyerOption, setSelectedBuyerOption] = useState<BuyerOption>(
    props?.providedBuyerOption
      ? BUYER_OPTIONS[props?.providedBuyerOption]
      : BUYER_OPTIONS['1-buyer'],
  );
  const [paymentOption, setPaymentOption] = useState<PaymentOption>(defaultPaymentOption);
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [showAcknowledgment, setShowAcknowledgment] = useState(false);

  // Safety net: Sync selectedTab when client changes or partner demographics become available/unavailable
  useEffect(() => {
    if (!props?.providedSelectedTab) {
      const newTab = hasPartnerWithDemographics ? 'joint' : 'single';
      setSelectedTab(newTab);
    }
  }, [client?.clientId, hasPartnerWithDemographics, props?.providedSelectedTab]);

  const handleTabChange = (tabValue: string) => {
    setSelectedTab(tabValue as ShopInsuranceTab);
    setExpandedIndices([]);
  };

  const handleCardToggle = (index: number) => {
    setExpandedIndices(prevIndices => {
      if (prevIndices.includes(index)) {
        return prevIndices.filter(i => i !== index);
      } else {
        return [...prevIndices, index];
      }
    });
  };

  const handleAcknowledgment = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      localStorage.setItem('shopInsuranceAcknowledgmentDismissed', 'true');
    }
    setHasAcknowledged(true);
    setShowAcknowledgment(false);
  };

  const handlePaymentOptionChange = (value: PaymentOption) => {
    setPaymentOption(value);
  };

  const handleBuyerOptionChange = (buyerKey: '1-buyer' | '2-buyer') => {
    setSelectedBuyerOption(BUYER_OPTIONS[buyerKey]);
  };

  const resetState = useCallback(() => {
    setExpandedIndices([]);
    setHasAcknowledged(false);
  }, []);

  return {
    selectedTab,
    selectedBuyerOption,
    paymentOption,
    expandedIndices,
    hasAcknowledged,
    showAcknowledgment,
    hasPartnerWithDemographics,
    setShowAcknowledgment,
    handleTabChange,
    handleCardToggle,
    handleAcknowledgment,
    handlePaymentOptionChange,
    handleBuyerOptionChange,
    resetState,
  };
}
